import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Menu,
  Badge,
  Tooltip,
  Stack,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Key as KeyIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  DarkMode as DarkModeIcon,
  Shield as ShieldIcon,
  History as HistoryIcon,
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  last_login: string | null;
  created_by: number | null;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
  created_at: string;
}

interface ActivityLogEntry {
  id: number;
  user_name: string;
  user_email: string;
  action: string;
  target_type: string;
  target_id: string;
  details: string;
  created_at: string;
  ip_address: string;
}

const Settings: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form states
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    status: 'active' as 'active' | 'inactive' | 'suspended'
  });
  
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [] as string[]
  });

  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Available permissions
  const availablePermissions = [
    'dashboard.view',
    'orders.view', 'orders.update', 'orders.create', 'orders.delete',
    'products.view', 'products.update', 'products.create', 'products.delete',
    'vendors.view', 'vendors.update', 'vendors.create', 'vendors.delete',
    'accounting.view', 'accounting.update',
    'users.view', 'users.create', 'users.update', 'users.delete',
    'roles.view', 'roles.create', 'roles.update', 'roles.delete',
    'activity.view',
    'settings.view', 'settings.update'
  ];

  // Load data
  useEffect(() => {
    if (currentTab === 0) loadUsers();
    if (currentTab === 1) loadRoles();
    if (currentTab === 3) loadActivity();
  }, [currentTab]);

  const loadUsers = async () => {
    if (!hasPermission('users.view')) return;
    
    setLoading(true);
    try {
      const response = await api.getUsers() as any;
      if (response.status === 'success') {
        setUsers(response.users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setSnackbar({ open: true, message: 'Failed to load users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    if (!hasPermission('roles.view')) return;
    
    setLoading(true);
    try {
      const response = await api.getRoles() as any;
      if (response.status === 'success') {
        setRoles(response.roles);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
      setSnackbar({ open: true, message: 'Failed to load roles', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    if (!hasPermission('activity.view')) return;
    
    setLoading(true);
    try {
      const response = await api.getActivity(100) as any;
      if (response.status === 'success') {
        setActivities(response.activities);
      }
    } catch (error) {
      console.error('Failed to load activity:', error);
      setSnackbar({ open: true, message: 'Failed to load activity log', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async () => {
    if (!userForm.name || !userForm.email || (!editingUser && !userForm.password)) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      if (editingUser) {
        // Update user
        const updateData: any = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          status: userForm.status
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        
        await api.updateUser(editingUser.id, updateData);
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
      } else {
        // Create user
        await api.createUser(userForm);
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
      }
      
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'employee', status: 'active' });
      loadUsers();
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error?.response?.data?.error || 'Failed to save user', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSubmit = async () => {
    if (!roleForm.name || !roleForm.display_name) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const roleData = {
        ...roleForm,
        permissions: JSON.stringify(roleForm.permissions)
      };

      if (editingRole) {
        await api.updateRole(editingRole.id, roleData);
        setSnackbar({ open: true, message: 'Role updated successfully', severity: 'success' });
      } else {
        await api.createRole(roleData);
        setSnackbar({ open: true, message: 'Role created successfully', severity: 'success' });
      }
      
      setRoleDialogOpen(false);
      setEditingRole(null);
      setRoleForm({ name: '', display_name: '', description: '', permissions: [] });
      loadRoles();
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error?.response?.data?.error || 'Failed to save role', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setLoading(true);
    try {
      await api.deleteUser(userId);
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      loadUsers();
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error?.response?.data?.error || 'Failed to delete user', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    
    setLoading(true);
    try {
      await api.deleteRole(roleId);
      setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
      loadRoles();
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error?.response?.data?.error || 'Failed to delete role', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const openUserDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        status: user.status
      });
    } else {
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'employee', status: 'active' });
    }
    setUserDialogOpen(true);
  };

  const openRoleDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        display_name: role.display_name,
        description: role.description,
        permissions: role.permissions
      });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', display_name: '', description: '', permissions: [] });
    }
    setRoleDialogOpen(true);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'employee': return 'info';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!hasPermission('settings.view')) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to access settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Settings & Administration
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {hasPermission('users.view') && (
            <Tab 
              icon={<PersonIcon />} 
              label={
                <Badge badgeContent={users.length} color="primary">
                  Users
                </Badge>
              } 
            />
          )}
          {hasPermission('roles.view') && (
            <Tab 
              icon={<GroupIcon />} 
              label={
                <Badge badgeContent={roles.length} color="secondary">
                  Roles & Permissions
                </Badge>
              } 
            />
          )}
          <Tab icon={<SettingsIcon />} label="System Settings" />
          {hasPermission('activity.view') && (
            <Tab 
              icon={<HistoryIcon />} 
              label={
                <Badge badgeContent={activities.length} color="info">
                  Activity Log
                </Badge>
              } 
            />
          )}
        </Tabs>

        {/* Users Tab */}
        {currentTab === 0 && hasPermission('users.view') && (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">User Management</Typography>
              {hasPermission('users.create') && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => openUserDialog()}
                >
                  Add User
                </Button>
              )}
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: getRoleColor(user.role) + '.main' }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">{user.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.role} 
                            color={getRoleColor(user.role) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.status} 
                            color={getStatusColor(user.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {user.last_login ? formatDate(user.last_login) : 'Never'}
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => {
                              setUserMenuAnchor(e.currentTarget);
                              setSelectedUser(user);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Roles Tab */}
        {currentTab === 1 && hasPermission('roles.view') && (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Role & Permission Management</Typography>
              {hasPermission('roles.create') && (
                <Button
                  variant="contained"
                  startIcon={<GroupAddIcon />}
                  onClick={() => openRoleDialog()}
                >
                  Add Role
                </Button>
              )}
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {roles.map((role) => (
                  <Grid item xs={12} md={6} lg={4} key={role.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                          <Box>
                            <Typography variant="h6">{role.display_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {role.description}
                            </Typography>
                          </Box>
                          {hasPermission('roles.update') && (
                            <IconButton size="small" onClick={() => openRoleDialog(role)}>
                              <EditIcon />
                            </IconButton>
                          )}
                        </Box>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          Permissions ({role.permissions.length}):
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {role.permissions.slice(0, 3).map((permission) => (
                            <Chip 
                              key={permission} 
                              label={permission} 
                              size="small" 
                              variant="outlined"
                            />
                          ))}
                          {role.permissions.length > 3 && (
                            <Chip 
                              label={`+${role.permissions.length - 3} more`} 
                              size="small" 
                              color="primary"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* System Settings Tab */}
        {currentTab === 2 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>System Settings</Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Enable email notifications for new orders and user activities"
                />
                <Switch edge="end" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <DarkModeIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Dark Mode"
                  secondary="Use dark theme throughout the application"
                />
                <Switch edge="end" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <LanguageIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Language"
                  secondary="Current language: English"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <ShieldIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Session Timeout"
                  secondary="Users will be logged out after 24 hours of inactivity"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <LockIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Password Policy"
                  secondary="Enforce strong passwords for all users"
                />
                <Switch edge="end" />
              </ListItem>
            </List>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              About
            </Typography>
            <Paper sx={{ p: 3 }}>
              <Typography variant="body1" paragraph>
                Oroud ERP System v2.0.0
              </Typography>
              <Typography variant="body2" color="textSecondary">
                © 2024 Oroud. All rights reserved. Authentication & Role Management System Enabled.
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Activity Log Tab */}
        {currentTab === 3 && hasPermission('activity.view') && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>User Activity Log</Typography>
            
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{activity.user_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {activity.user_email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={activity.action} 
                            size="small"
                            color={activity.action.includes('login') ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {activity.target_type} #{activity.target_id}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={activity.details}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {activity.details}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatDate(activity.created_at)}</TableCell>
                        <TableCell>{activity.ip_address}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Full Name"
            value={userForm.name}
            onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
            type={showPassword ? 'text' : 'password'}
            value={userForm.password}
            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
            margin="normal"
            required={!editingUser}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <HideIcon /> : <ViewIcon />}
                </IconButton>
              )
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={userForm.role}
              onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem key={role.name} value={role.name}>
                  {role.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={userForm.status}
              onChange={(e) => setUserForm({ ...userForm, status: e.target.value as any })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUserSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Name (system name)"
            value={roleForm.name}
            onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
            margin="normal"
            required
            helperText="Lowercase, no spaces (e.g., 'sales_manager')"
          />
          <TextField
            fullWidth
            label="Display Name"
            value={roleForm.display_name}
            onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={roleForm.description}
            onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Permissions:
          </Typography>
          <Grid container spacing={1}>
            {availablePermissions.map((permission) => (
              <Grid item xs={6} sm={4} key={permission}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={roleForm.permissions.includes(permission)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRoleForm({
                            ...roleForm,
                            permissions: [...roleForm.permissions, permission]
                          });
                        } else {
                          setRoleForm({
                            ...roleForm,
                            permissions: roleForm.permissions.filter(p => p !== permission)
                          });
                        }
                      }}
                    />
                  }
                  label={permission}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRoleSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingRole ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
      >
        {hasPermission('users.update') && (
          <MenuItem onClick={() => {
            setUserMenuAnchor(null);
            openUserDialog(selectedUser!);
          }}>
            <EditIcon sx={{ mr: 1 }} />
            Edit User
          </MenuItem>
        )}
        {hasPermission('users.delete') && selectedUser?.id !== user?.id && (
          <MenuItem onClick={() => {
            setUserMenuAnchor(null);
            handleDeleteUser(selectedUser!.id);
          }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete User
          </MenuItem>
        )}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 